import { Link } from "react-router-dom";
import {
  TbChartArrowsVertical,
  TbHeartHandshake,
  TbRocket,
  TbRosetteDiscountCheck,
} from "react-icons/tb";
import { PageSeo } from "../seo";
import { L } from "../../../../constants/baysportLabels";
import { TRIAL_DAYS } from "../../../../constants/trial";
import { LEGAL_OPERATOR } from "../../../../constants/legalDocuments";
import { LEGAL_PATHS } from "../../../../constants/legalPaths";
import { IMG, PATHS } from "../nav";
import site from "../site.module.css";
import h from "./home.module.css";
import { FaqSection, PageHero, RegisterSection, Title } from "./parts";

const PRINCIPLES = [
  [
    TbRocket,
    "Без внедрения",
    "Клуб запускается сам, за вечер. Без интеграторов, долгого обучения и обязательных созвонов.",
  ],
  [
    TbRosetteDiscountCheck,
    "Честная цена",
    `Платите за клуб, а не за каждого тренера. ${TRIAL_DAYS} дней бесплатно, без привязки карты.`,
  ],
  [
    TbChartArrowsVertical,
    "Растём вместе с клубом",
    "От одного тренера до нескольких залов: меняете тариф, а клиенты и история остаются на месте.",
  ],
  [
    TbHeartHandshake,
    "На связи",
    "Помогаем перенести базу и настроить клуб. Предложения клубов становятся новыми функциями.",
  ],
];

const FACTS = [
  ["Работает в браузере", "На компьютере и телефоне — ничего не нужно устанавливать."],
  ["Приложение для клиентов", "Открывается по ссылке, без App Store и Google Play."],
  ["Оплата напрямую клубу", "Через ЮKassa или Т-Банк — деньги приходят на ваш счёт."],
];

const FAQ = [
  [
    "Как связаться с командой?",
    `Напишите на ${LEGAL_OPERATOR.supportEmail} — ответим по запуску, переносу базы и работе сервиса.`,
  ],
  [
    "Можно ли предложить новую функцию?",
    "Да. Напишите, какой задачи не хватает вашему клубу, — мы собираем такие запросы и учитываем их в развитии сервиса.",
  ],
  [
    "Подойдёт ли сервис крупному клубу?",
    "Да. Тариф «Про» рассчитан на команды до 30 тренеров, до 1 500 клиентов и до 10 залов.",
  ],
];

export default function AboutPage() {
  return (
    <>
      <PageSeo page="about" faq={FAQ} />

      <PageHero
        pill={`О компании ${L.productName}`}
        lines={["Сервис для тех,", "кто тренирует."]}
        lead={`${L.productName} — облачный сервис для спортивных клубов, секций и студий. Чтобы команда занималась спортом, а не таблицами.`}
        actions={
          <>
            <a href="#register" className={`${site.btn} ${site.btnLg}`}>
              Создать клуб бесплатно
            </a>
            <a href="#requisites" className={h.link}>
              Реквизиты ↗
            </a>
          </>
        }
        visual={
          <div className={h.collagePhoto}>
            <img src={IMG.a} alt="" />
          </div>
        }
      />

      <section className={h.section}>
        <div className={`${h.wrap} ${h.split}`}>
          <div className={h.splitPhoto}>
            <img
              src={IMG.b}
              alt="Тренер проводит занятие с группой в зале"
              loading="lazy"
            />
          </div>
          <div>
            <p className={h.kicker}>01 / Зачем мы это делаем</p>
            <Title lines={["Тренер ведёт группу.", "Учёт идёт сам."]} />
            <p className={h.sub}>
              Отметили, кто пришёл — занятие списалось с абонемента. Родитель
              видит посещение в приложении, о переносе приходит уведомление.
            </p>
            <p className={h.sub}>
              {L.productName} собирает расписание, журнал, оплаты и переписку
              с клубом в одном месте, чтобы после тренировки ничего не
              переписывать.
            </p>
            <div className={h.facts}>
              {FACTS.map(([t, d]) => (
                <div key={t}>
                  <b>{t}</b>
                  <span>{d}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className={`${h.section} ${h.soft}`}>
        <div className={h.wrap}>
          <div className={h.head}>
            <p className={h.kicker}>02 / Принципы</p>
            <Title lines={["Просто начать.", "Легко расти."]} />
          </div>
          <div className={h.grid4}>
            {PRINCIPLES.map(([Icon, t, d]) => (
              <article key={t} className={h.card}>
                <span className={`${h.audIcon} ${h.principleIcon}`} aria-hidden>
                  <Icon size={22} />
                </span>
                <h3>{t}</h3>
                <p>{d}</p>
              </article>
            ))}
          </div>
          <div className={h.ask}>
            <div>
              <h3>Посмотрите, что умеет сервис</h3>
              <p>Расписание, журнал, абонементы, финансы и приложение клуба.</p>
            </div>
            <Link to={PATHS.features} className={site.btn}>
              Возможности
            </Link>
          </div>
        </div>
      </section>

      <section className={h.section} id="requisites">
        <div className={h.wrap}>
          <div className={h.head}>
            <p className={h.kicker}>03 / Реквизиты</p>
            <Title lines={["Работаем открыто"]} />
          </div>
          <div className={h.reqs}>
            <dl className={`${h.card} ${h.reqList}`}>
              <div>
                <dt>Исполнитель</dt>
                <dd>{LEGAL_OPERATOR.legalName}</dd>
              </div>
              <div>
                <dt>ИНН</dt>
                <dd>{LEGAL_OPERATOR.inn}</dd>
              </div>
              <div>
                <dt>ОГРНИП</dt>
                <dd>{LEGAL_OPERATOR.ogrnOrOgrnip.replace(/^ОГРНИП\s*/, "")}</dd>
              </div>
              <div>
                <dt>Почта</dt>
                <dd>
                  <a href={`mailto:${LEGAL_OPERATOR.supportEmail}`}>
                    {LEGAL_OPERATOR.supportEmail}
                  </a>
                </dd>
              </div>
            </dl>
            <div className={`${h.card} ${h.docs}`}>
              <h3>Документы</h3>
              <Link to={LEGAL_PATHS.offer}>Пользовательское соглашение (оферта) ↗</Link>
              <Link to={LEGAL_PATHS.privacy}>Политика конфиденциальности ↗</Link>
              <Link to={LEGAL_PATHS.payments}>Оплата и возврат ↗</Link>
              <Link to={LEGAL_PATHS.cookies}>Политика cookies ↗</Link>
            </div>
          </div>
        </div>
      </section>

      <FaqSection kicker="04 / Вопросы и ответы" items={FAQ} soft />

      <RegisterSection
        kicker="05 / Регистрация"
        title={["Попробуйте", `${L.productName} на своём клубе`]}
      />
    </>
  );
}
